import React from 'react';
import { EyeOff, ShieldCheck } from 'lucide-react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Route, Switch, useRouteMatch } from 'react-router-dom';

import { SignUp } from '../signUp';
import { SignIn } from '../signIn';
import { Profile } from '../profile';

export const Auth = ({ sharedList }) => {
  const { path, url } = useRouteMatch();

  return (
    <Flex
      mt={['40px', '40px', '0']}
      bg="neutral.0"
      flexDirection={['column', 'column', 'row']}
      sx={{
        gap: '24px',
        boxShadow: 'medium',
        borderRadius: 'default',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 5,
      }}
    >
      <Box p="24px" maxWidth="462px" sx={{ position: 'relative' }}>
        <Switch>
          <Route exact path={`${path}`}>
            <SignUp routePath={url} sharedList={sharedList} />
          </Route>
          <Route exact path={`${path}/sign-in`}>
            <SignIn routePath={url} sharedList={sharedList} />
          </Route>
          <Route exact path={`${path}/profile`}>
            <Profile sharedList={sharedList} />
          </Route>
        </Switch>
      </Box>
      <Box p="24px" bg="neutral.20" maxWidth={['100%', '100%', '320px']}>
        <Text
          mt="16px"
          fontSize="12px"
          fontWeight="medium"
          lineHeight="140%"
          sx={{ letterSpacing: '1px', textTransform: 'uppercase' }}
        >
          How it works
        </Text>
        <Box mt="16px">
          <Flex as="ul" flexDirection="column" sx={{ listStyle: 'none', padding: 0, margin: 0, gap: '16px' }}>
            <Flex
              alignItems="flex-start"
              sx={{
                gap: '8px',
                position: 'relative',
                '&::after': {
                  content: "''",
                  backgroundColor: 'neutral.300',
                  width: '2px',
                  height: 'calc(100% - 24px + 16px)',
                  position: 'absolute',
                  left: '11px',
                  top: '24px',
                },
              }}
            >
              <Flex
                width="24px"
                height="24px"
                bg="neutral.300"
                alignItems="center"
                justifyContent="center"
                sx={{ borderRadius: 'circle' }}
                flex="0 0 auto"
              >
                <Text fontSize="14px" fontWeight="medium" color="neutral.0">
                  1
                </Text>
              </Flex>
              <Box>
                <Text fontSize="14px" fontWeight="medium" color="text.dark">
                  Connect your LinkedIn
                </Text>
                <Text as="p" fontSize="14px" mt="4px">
                  Sync your contacts privately–only you can see them.
                </Text>
              </Box>
            </Flex>
            <Flex
              alignItems="flex-start"
              sx={{
                gap: '8px',
                position: 'relative',
                '&::after': {
                  content: "''",
                  backgroundColor: 'neutral.300',
                  width: '2px',
                  height: 'calc(100% - 24px + 16px)',
                  position: 'absolute',
                  left: '11px',
                  top: '24px',
                },
              }}
            >
              <Flex
                width="24px"
                height="24px"
                bg="neutral.300"
                alignItems="center"
                justifyContent="center"
                sx={{ borderRadius: 'circle' }}
                flex="0 0 auto"
              >
                <Text fontSize="14px" fontWeight="medium" color="neutral.0">
                  2
                </Text>
              </Flex>
              <Box>
                <Text fontSize="14px" fontWeight="medium" color="text.dark">
                  Find opportunities
                </Text>
                <Text fontSize="14px" mt="4px">
                  Identify key connections.
                </Text>
              </Box>
            </Flex>
            <Flex alignItems="flex-start" sx={{ gap: '8px' }}>
              <Flex
                width="24px"
                height="24px"
                bg="neutral.300"
                alignItems="center"
                justifyContent="center"
                sx={{ borderRadius: 'circle' }}
                flex="0 0 auto"
              >
                <Text fontSize="14px" fontWeight="medium" color="neutral.0">
                  3
                </Text>
              </Flex>
              <Box>
                <Text fontSize="14px" fontWeight="medium" color="text.dark">
                  Make intros
                </Text>
                <Text fontSize="14px" mt="4px">
                  {`Choose the best opportunities and support ${sharedList.collection?.name}.`}
                </Text>
              </Box>
            </Flex>
          </Flex>
        </Box>
        <Text
          mt="40px"
          fontSize="12px"
          fontWeight="medium"
          lineHeight="140%"
          sx={{ letterSpacing: '1px', textTransform: 'uppercase' }}
        >
          Your data is safe and private
        </Text>
        <Flex as="ul" mt="16px" flexDirection="column" sx={{ listStyle: 'none', padding: 0, margin: 0, gap: '16px' }}>
          <Flex as="li" alignItems="flex-start" sx={{ gap: '8px', color: 'neutral.500' }}>
            <Box as={EyeOff} width="24px" height="24px" aria-hidden="true" flex="0 0 auto" />
            <Text fontSize="14px" color="text.main">
              Your contacts stay visible only to you unless you choose to share them.
            </Text>
          </Flex>
          <Flex as="li" alignItems="flex-start" sx={{ gap: '8px', color: 'neutral.500' }}>
            <Box as={ShieldCheck} width="24px" height="24px" aria-hidden="true" flex="0 0 auto" />
            <Text fontSize="14px" color="text.main">
              We only collect who you are connected to, nothing else.
            </Text>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

Auth.propTypes = {
  sharedList: PropTypes.object.isRequired,
};
